import React, { useState } from 'react';
import { X, Copy, CheckCircle, AlertTriangle, Key } from 'lucide-react';

interface ApiKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
  apiKey: string;
  apiSecret?: string; // Optional - no longer used in new implementation
  keyName?: string; // Optional - name of the API key
}

const ApiKeyModal: React.FC<ApiKeyModalProps> = ({ isOpen, onClose, apiKey, keyName }) => {
  const [copiedKey, setCopiedKey] = useState(false);

  if (!isOpen) return null;

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(true);
      setTimeout(() => setCopiedKey(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="relative w-full max-w-2xl rounded-2xl border p-8" style={{backgroundColor: 'rgba(0, 0, 0, 0.95)', borderColor: 'rgba(173, 0, 0, 0.5)'}}>
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-lg hover:bg-white/10 transition-colors"
          aria-label="Close modal"
        >
          <X className="w-6 h-6 text-white/70 hover:text-white" />
        </button>

        {/* Header */}
        <div className="flex items-center space-x-3 mb-6">
          <div className="p-3 rounded-xl bg-gradient-to-br from-red-600 to-red-800">
            <Key className="w-8 h-8 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">API Key Created</h2>
            <p className="text-white/70">{keyName ? `Key: ${keyName}` : 'Save this key securely'}</p>
          </div>
        </div>

        {/* Warning Banner */}
        <div className="mb-6 p-4 rounded-xl border flex items-start space-x-3" style={{backgroundColor: 'rgba(234, 179, 8, 0.1)', borderColor: 'rgba(234, 179, 8, 0.3)'}}>
          <AlertTriangle className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-semibold text-yellow-200 mb-1">Important: Save Your API Key</h3>
            <p className="text-sm text-yellow-100/80">
              This is the only time you will see your full API key. Make sure to copy and store it in a secure location.
              You will need it to authenticate API requests.
            </p>
          </div>
        </div>

        {/* API Key */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-white/80 mb-2">API Key</label>
          <div className="flex items-center space-x-2">
            <div className="flex-1 px-4 py-3 rounded-xl border bg-black/30 backdrop-blur-xl text-white font-mono text-sm break-all" style={{borderColor: 'rgba(255, 255, 255, 0.2)'}}>
              {apiKey}
            </div>
            <button
              onClick={() => copyToClipboard(apiKey)}
              className="px-4 py-3 rounded-xl transition-all flex items-center space-x-2 border"
              style={{
                background: copiedKey ? 'rgba(34, 197, 94, 0.2)' : 'rgba(173, 0, 0, 0.2)',
                borderColor: copiedKey ? 'rgba(34, 197, 94, 0.5)' : 'rgba(173, 0, 0, 0.3)'
              }}
              title="Copy API Key"
            >
              {copiedKey ? (
                <CheckCircle className="w-5 h-5 text-green-400" />
              ) : (
                <Copy className="w-5 h-5 text-white/70" />
              )}
            </button>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end pt-4 border-t" style={{borderColor: 'rgba(255, 255, 255, 0.1)'}}>
          <button
            onClick={onClose}
            className="px-6 py-3 rounded-xl transition-all transform hover:scale-105 font-medium text-white"
            style={{background: 'linear-gradient(135deg, #AD0000 0%, #9A1220 100%)'}}
            onMouseEnter={(e) => (e.target as HTMLElement).style.background = 'linear-gradient(135deg, #9A1220 0%, #AD0000 100%)'}
            onMouseLeave={(e) => (e.target as HTMLElement).style.background = 'linear-gradient(135deg, #AD0000 0%, #9A1220 100%)'}
          >
            I've Saved My API Key
          </button>
        </div>

        {/* Additional Info */}
        <div className="mt-6 p-4 rounded-xl" style={{backgroundColor: 'rgba(173, 0, 0, 0.1)'}}>
          <h4 className="text-sm font-semibold text-white mb-2">How to use your API key:</h4>
          <ul className="text-sm text-white/70 space-y-1 list-disc list-inside">
            <li>Include your API Key in the request header as <code className="text-red-400">X-API-Key</code></li>
            <li>Keep your API key secure and never share it publicly</li>
            <li>You can generate a new API key from your settings if this one is compromised</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ApiKeyModal;
